import { AlertTriangle } from 'lucide-react';
import { useMemo, useState } from 'react';
import type {
  ScheduleItemImportOptions,
  ScheduleItemRowStatus,
  ScheduleItemsImportPreview,
} from '@obraplan/core/application/contracts/schedule-items';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { cn } from '@/lib/cn';
import { formatBusinessDate } from '@/lib/format';
import {
  FILE_KIND_LABEL,
  LINE_STATUS_LABEL,
  countLines,
  describeFile,
  serviceKey,
  type LoadedScheduleFile,
  type PreparedImport,
  type PreviewLine,
} from './import-model';

/** O que o usuário escolheu, ainda sem aplicar, para um nome que o de-para não conhece. */
export interface ChoiceDraft {
  readonly disciplineName: string;
  /** Vazio: a frente inteira. */
  readonly serviceName: string;
}

export interface ConferenciaProps {
  readonly file: LoadedScheduleFile;
  readonly towerName: string;
  readonly prepared: PreparedImport;
  /** As escolhas digitadas, por chave do nome. */
  readonly choices: Readonly<Record<string, ChoiceDraft>>;
  readonly onChoice: (sourceKey: string, draft: ChoiceDraft) => void;
  readonly onApplyChoices: () => void;
  readonly disciplineSuggestions: readonly string[];
  readonly serviceSuggestions: (disciplineName: string) => readonly string[];
  /** A prévia do servidor; `undefined` enquanto ela não chega. */
  readonly preview: ScheduleItemsImportPreview | undefined;
  /** As linhas enviadas, já juntas à resposta. */
  readonly lines: readonly PreviewLine[] | undefined;
  readonly isPreviewing: boolean;
  readonly previewError: string | undefined;
  readonly options: ScheduleItemImportOptions;
  readonly onOptions: (next: ScheduleItemImportOptions) => void;
  /** Serviços que o usuário não aprovou, pela `serviceKey`. */
  readonly declined: ReadonlySet<string>;
  readonly onApproval: (key: string, approved: boolean) => void;
  readonly onApproveAll: (approved: boolean) => void;
  /** Criar serviço exige `takeoff.manage` além de `schedule.import`. */
  readonly canCreateServices: boolean;
}

type Filter = ScheduleItemRowStatus | 'ALL' | 'EXCLUDED';

const STATUS_TONE: Readonly<Record<ScheduleItemRowStatus, 'ok' | 'info' | 'neutral' | 'danger'>> = {
  CREATE: 'ok',
  UPDATE: 'info',
  UNCHANGED: 'neutral',
  ERROR: 'danger',
};

/** Linhas que a tabela mostra de uma vez: o resto fica atrás do filtro. */
const VISIBLE_LINES = 200;

/**
 * O segundo passo da importação: a conferência.
 *
 * É o passo que justifica os outros — **nada é gravado até a confirmação** (RN-325).
 * Mostra, nesta ordem, o que o arquivo trouxe que o sistema não reconhece (e pede a
 * escolha), o que falta no cadastro da obra (com as quatro opções de criar, todas
 * desligadas por padrão, RN-035, e os serviços novos aprovados um a um), a contagem do que
 * a importação faria e cada linha com o motivo, quando recusada.
 */
export function ImportConferencia(props: ConferenciaProps) {
  const { file, prepared, preview, lines } = props;
  const [filter, setFilter] = useState<Filter>('ALL');

  const counts = useMemo(() => (lines === undefined ? undefined : countLines(lines)), [lines]);
  const shown = useMemo(() => {
    if (lines === undefined) return [];
    const matching =
      filter === 'ALL'
        ? lines
        : filter === 'EXCLUDED'
          ? lines.filter((line) => line.excluded)
          : lines.filter((line) => !line.excluded && line.status === filter);
    return matching.slice(0, VISIBLE_LINES);
  }, [lines, filter]);

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm">
        <strong>{file.fileName}</strong>{' '}
        <span className="text-(--color-content-muted)">
          · {FILE_KIND_LABEL[file.kind]} · {describeFile(file)} → {prepared.rows.length}{' '}
          {prepared.rows.length === 1 ? 'item' : 'itens'}
          {file.kind === 'GANTTPRO' && props.towerName.trim() !== '' && (
            <> para {props.towerName}</>
          )}
        </span>
      </p>

      {prepared.unresolved.length > 0 && <Desconhecidos {...props} />}

      {prepared.issues.length > 0 && (
        <section aria-labelledby="conf-issues" className="flex flex-col gap-2">
          <h3 id="conf-issues" className="flex items-center gap-2 text-sm font-medium">
            <AlertTriangle className="size-4 text-(--color-state-warn)" aria-hidden="true" />
            Linhas do arquivo que não viraram item ({prepared.issues.length})
          </h3>
          <ul className="max-h-32 overflow-y-auto text-xs text-(--color-content-muted)">
            {prepared.issues.slice(0, 20).map((issue, index) => (
              <li key={`${String(issue.rowNumber)}-${String(index)}`}>
                Linha {issue.rowNumber}: {issue.message}
              </li>
            ))}
            {prepared.issues.length > 20 && <li>… e mais {prepared.issues.length - 20}.</li>}
          </ul>
        </section>
      )}

      {props.previewError !== undefined && (
        <p role="alert" className="text-sm text-(--color-state-danger)">
          {props.previewError}
        </p>
      )}

      {preview !== undefined && <Cadastro {...props} preview={preview} />}

      {counts === undefined ? (
        props.previewError === undefined && (
          <p role="status" className="text-sm text-(--color-content-muted)">
            {props.isPreviewing
              ? 'Conferindo com o cadastro da obra…'
              : 'Sem linhas para conferir.'}
          </p>
        )
      ) : (
        <section aria-labelledby="conf-lines" className="flex flex-col gap-3">
          <h3 id="conf-lines" className="sr-only">
            Linhas da importação
          </h3>
          <div
            className="flex flex-wrap items-center gap-2"
            role="group"
            aria-label="Filtrar linhas"
          >
            {(
              [
                ['ALL', 'Todas', lines?.length ?? 0],
                ['CREATE', 'Criar', counts.create],
                ['UPDATE', 'Atualizar', counts.update],
                ['UNCHANGED', 'Iguais', counts.unchanged],
                ['ERROR', 'Com erro', counts.error],
                ...(counts.excluded > 0 ? [['EXCLUDED', 'Fora', counts.excluded] as const] : []),
              ] as const
            ).map(([value, label, count]) => (
              <button
                key={value}
                type="button"
                aria-pressed={filter === value}
                onClick={() => setFilter(value)}
                className={cn(
                  'min-h-9 rounded-full border px-3 py-1 text-xs md:min-h-0',
                  filter === value
                    ? 'border-(--color-brand) font-medium text-(--color-brand)'
                    : 'border-(--color-border-subtle) text-(--color-content-muted)',
                )}
              >
                {label} ({count})
              </button>
            ))}
          </div>

          {shown.length === 0 ? (
            <p className="text-sm text-(--color-content-muted)">Nenhuma linha neste filtro.</p>
          ) : (
            <TableWrapper className="max-h-72 overflow-y-auto">
              <Table>
                <TableHeader sticky>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-14">Linha</TableHead>
                    <TableHead>Situação</TableHead>
                    <TableHead>Pavimento</TableHead>
                    <TableHead>Disciplina</TableHead>
                    <TableHead>Serviço</TableHead>
                    <TableHead>Trecho</TableHead>
                    <TableHead>Início</TableHead>
                    <TableHead>Fim</TableHead>
                    <TableHead>Motivo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shown.map((line) => (
                    <TableRow key={line.index}>
                      <TableCell className="tabular text-(--color-content-muted)">
                        {line.row.rowNumber}
                      </TableCell>
                      <TableCell>
                        {line.excluded ? (
                          <Badge tone="neutral">Fora</Badge>
                        ) : (
                          <Badge tone={STATUS_TONE[line.status]}>
                            {LINE_STATUS_LABEL[line.status]}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="max-w-32 truncate">{line.row.floorName}</TableCell>
                      <TableCell className="max-w-36 truncate">{line.row.disciplineName}</TableCell>
                      <TableCell className="max-w-40 truncate">
                        {line.row.serviceName ?? (
                          <span className="italic text-(--color-content-muted)">
                            Frente inteira
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="max-w-28 truncate">{line.row.segment ?? '—'}</TableCell>
                      <TableCell className="tabular whitespace-nowrap">
                        {formatBusinessDate(line.row.plannedStart)}
                      </TableCell>
                      <TableCell className="tabular whitespace-nowrap">
                        {formatBusinessDate(line.row.plannedEnd)}
                      </TableCell>
                      <TableCell className="max-w-72 text-xs text-(--color-content-muted)">
                        {line.excluded
                          ? 'O serviço novo não foi aprovado: a linha não entra.'
                          : line.issues.map((issue) => issue.message).join(' ')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableWrapper>
          )}
          {lines !== undefined && shown.length === VISIBLE_LINES && (
            <p className="text-xs text-(--color-content-muted)">
              Mostrando as primeiras {VISIBLE_LINES} linhas deste filtro.
            </p>
          )}
        </section>
      )}
    </div>
  );
}

// ── Nomes que o de-para não conhece ───────────────────────────────────────────────────

function Desconhecidos({
  prepared,
  choices,
  onChoice,
  onApplyChoices,
  disciplineSuggestions,
  serviceSuggestions,
}: ConferenciaProps) {
  const ready = prepared.unresolved.some(
    (name) => (choices[name.sourceKey]?.disciplineName.trim() ?? '') !== '',
  );

  return (
    <section aria-labelledby="conf-unknown" className="flex flex-col gap-3">
      <div>
        <h3 id="conf-unknown" className="text-sm font-medium">
          Nomes que o sistema ainda não conhece ({prepared.unresolved.length})
        </h3>
        <p className="text-xs text-(--color-content-muted)">
          Escolha a disciplina e o serviço de cada um; deixe o serviço em branco para a frente
          inteira. Até lá, as tarefas com estes nomes ficam de fora.
        </p>
      </div>

      <datalist id="conf-disciplines">
        {disciplineSuggestions.map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>

      <ul className="flex flex-col gap-3">
        {prepared.unresolved.map((name, index) => {
          const draft = choices[name.sourceKey] ?? { disciplineName: '', serviceName: '' };
          const disciplineId = `conf-d-${String(index)}`;
          const serviceId = `conf-s-${String(index)}`;
          const listId = `conf-sl-${String(index)}`;
          return (
            <li
              key={name.sourceKey}
              className="grid gap-2 rounded-md border border-(--color-border-subtle) p-3 sm:grid-cols-2"
            >
              <div className="sm:col-span-2">
                <p className="text-sm font-medium">{name.sourceKey}</p>
                <p className="text-xs text-(--color-content-muted)">
                  {name.count} {name.count === 1 ? 'tarefa' : 'tarefas'} · ex.: {name.sample}
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor={disciplineId} className="text-xs font-medium">
                  Disciplina de {name.sourceKey}
                </label>
                <Input
                  id={disciplineId}
                  list="conf-disciplines"
                  value={draft.disciplineName}
                  onChange={(event) =>
                    onChoice(name.sourceKey, { ...draft, disciplineName: event.target.value })
                  }
                />
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor={serviceId} className="text-xs font-medium">
                  Serviço de {name.sourceKey}
                </label>
                <Input
                  id={serviceId}
                  list={listId}
                  placeholder="(frente inteira)"
                  value={draft.serviceName}
                  onChange={(event) =>
                    onChoice(name.sourceKey, { ...draft, serviceName: event.target.value })
                  }
                />
                <datalist id={listId}>
                  {serviceSuggestions(draft.disciplineName).map((service) => (
                    <option key={service} value={service} />
                  ))}
                </datalist>
              </div>
            </li>
          );
        })}
      </ul>

      <div>
        <Button type="button" variant="secondary" disabled={!ready} onClick={onApplyChoices}>
          Aplicar escolhas
        </Button>
      </div>
    </section>
  );
}

// ── O que falta no cadastro ───────────────────────────────────────────────────────────

/** Quantos nomes a lista mostra antes do "e mais N". */
const LISTED = 12;

function Cadastro({
  preview,
  prepared,
  options,
  onOptions,
  declined,
  onApproval,
  onApproveAll,
  canCreateServices,
}: ConferenciaProps & { readonly preview: ScheduleItemsImportPreview }) {
  const { missing } = preview;
  const nothing =
    missing.floors.length === 0 &&
    missing.disciplines.length === 0 &&
    missing.services.length === 0 &&
    missing.activities === 0;

  if (nothing) {
    return (
      <p className="text-sm text-(--color-content-muted)">
        O cadastro da obra já tem os pavimentos, as frentes, as disciplinas e os serviços que o
        arquivo cita.
      </p>
    );
  }

  const pending = new Map(
    prepared.pendingApproval.flatMap((entry) =>
      entry.serviceName === null
        ? []
        : [[serviceKey(entry.disciplineName, entry.serviceName), entry] as const],
    ),
  );
  const approvedServices = missing.services.filter(
    (service) => !declined.has(serviceKey(service.disciplineName, service.serviceName)),
  ).length;

  return (
    <fieldset className="flex flex-col gap-3 rounded-md border border-(--color-border-subtle) p-3">
      <legend className="px-1 text-sm font-medium">O que falta no cadastro da obra</legend>
      <p className="text-xs text-(--color-content-muted)">
        Nada é criado sem a sua escolha. Sem marcar, as linhas que dependem do que falta ficam de
        fora, com o motivo.
      </p>

      {missing.floors.length > 0 && (
        <Option
          id="opt-floors"
          checked={options.createMissingFloors}
          onChange={(checked) => onOptions({ ...options, createMissingFloors: checked })}
          label={`Criar os pavimentos que faltam (${String(missing.floors.length)})`}
        >
          {listed(
            missing.floors.map(
              (floor) =>
                `${floor.floorName}${floor.level === null ? '' : ` (nível ${String(floor.level)})`}`,
            ),
          )}
        </Option>
      )}

      {missing.activities > 0 && (
        <Option
          id="opt-activities"
          checked={options.createMissingActivities}
          onChange={(checked) => onOptions({ ...options, createMissingActivities: checked })}
          label={`Criar as frentes que faltam (${String(missing.activities)})`}
        >
          Uma frente é a disciplina num pavimento.
        </Option>
      )}

      {missing.disciplines.length > 0 && (
        <Option
          id="opt-disciplines"
          checked={options.createMissingDisciplines}
          onChange={(checked) => onOptions({ ...options, createMissingDisciplines: checked })}
          label={`Criar as disciplinas novas (${String(missing.disciplines.length)})`}
        >
          {listed(missing.disciplines)} — entram no catálogo da obra, não no global.
        </Option>
      )}

      {missing.services.length > 0 && (
        <Option
          id="opt-services"
          checked={options.createMissingServices}
          disabled={!canCreateServices}
          onChange={(checked) => onOptions({ ...options, createMissingServices: checked })}
          label={`Criar os serviços novos (${String(missing.services.length)})`}
        >
          {canCreateServices
            ? 'Entram no catálogo da obra. Marque ou desmarque cada um abaixo.'
            : 'Criar serviço é do cadastro do quantitativo, e o seu perfil não o administra.'}
        </Option>
      )}

      {options.createMissingServices && missing.services.length > 0 && (
        <div className="flex flex-col gap-2 rounded-md bg-(--color-surface-muted) p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-medium">
              Serviços aprovados: {approvedServices} de {missing.services.length}
            </p>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => onApproveAll(true)}>
                Aprovar todos
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => onApproveAll(false)}>
                Desmarcar todos
              </Button>
            </div>
          </div>
          <ul className="grid max-h-56 gap-1 overflow-y-auto sm:grid-cols-2">
            {missing.services.map((service) => {
              const key = serviceKey(service.disciplineName, service.serviceName);
              const entry = pending.get(key);
              const id = `approve-${key}`;
              return (
                <li key={key} className="flex items-start gap-2 text-sm">
                  <input
                    id={id}
                    type="checkbox"
                    className="mt-1"
                    checked={!declined.has(key)}
                    onChange={(event) => onApproval(key, event.target.checked)}
                  />
                  <label htmlFor={id} className="flex flex-col">
                    <span>
                      {service.disciplineName} · {service.serviceName}
                    </span>
                    {entry !== undefined && (
                      <span className="text-xs text-(--color-content-muted)">
                        {entry.items} {entry.items === 1 ? 'item' : 'itens'}
                        {entry.newDiscipline ? ' · disciplina nova' : ''}
                        {entry.note === undefined ? '' : ` · ${entry.note}`}
                      </span>
                    )}
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </fieldset>
  );
}

function listed(names: readonly string[]): string {
  const head = names.slice(0, LISTED).join(', ');
  return names.length > LISTED ? `${head} e mais ${String(names.length - LISTED)}` : head;
}

function Option({
  id,
  checked,
  disabled = false,
  onChange,
  label,
  children,
}: {
  readonly id: string;
  readonly checked: boolean;
  readonly disabled?: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly label: string;
  readonly children?: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2">
      <input
        id={id}
        type="checkbox"
        className="mt-1"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <div className="flex flex-col">
        <label htmlFor={id} className="text-sm font-medium">
          {label}
        </label>
        {children !== undefined && (
          <p className="text-xs text-(--color-content-muted)">{children}</p>
        )}
      </div>
    </div>
  );
}
